/* eslint-disable no-restricted-properties */
import isEmpty from 'lodash/isEmpty';
import intersection from 'lodash/intersection';
import React, { useCallback, useLayoutEffect } from 'react';

import authService from 'services/auth';

import NotFoundPage from 'shared/ui/NotFoundPage';
import { getQueryVariables, isLocalHostname, redirectToExternalUrl } from 'shared/functions';

import Input from 'containers/Input';
import { requestBackend } from 'utils/request';

import type { AuthMethodsType } from 'types/generic-context';

import { errorMessage } from './functions';

const SignInLocal = (props: any) => {
  const { accountTypes, redirectPath } = props;
  const query = getQueryVariables();

  const [password, setPassword] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState(null);
  const [authenticating, setAuthenticating] = React.useState(false);
  const [isAutofilled, setIsAutofilled] = React.useState(false);

  useLayoutEffect(() => {
    const checkAutofill = () => {
      const inputElements: any = document.getElementsByTagName('input');
      if (!inputElements.length) return;
      for (const input of inputElements) {
        if (!input.matches('*:-webkit-autofill')) {
          return;
        }
      }
      setIsAutofilled(true);
    };
    setTimeout(checkAutofill, 1000);
  }, []);

  const getProfile = useCallback(() => requestBackend('/authentication/my/profile'), []);

  const signInPassword = useCallback(
    () =>
      requestBackend('/authentication/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
        }),
      }),
    [email, password],
  );

  const authenticate = useCallback(
    async (evt) => {
      if (evt) {
        evt.preventDefault();
      }
      setMessage(null);
      setAuthenticating(true);

      try {
        const result: any = await signInPassword();

        if (result.user) {
          if (isEmpty(intersection(result.user.accountTypes, accountTypes))) {
            setMessage('This user is not authorized to access this application.');
          } else {
            const { userId, emails, accountTypes, name, activeAccountTypes } = result.user;
            authService.signIn({
              user: {
                userId,
                accountTypes,
                activeAccountTypes,
                firstName: name.firstName,
                lastName: name.lastName,
                email: emails.primary.emailAddress,
              },
              token: result.token,
            });

            const profile: any = await getProfile();

            authService.signIn({
              authTypes: profile.authMethods ? (Object.keys(profile.authMethods) as AuthMethodsType[]) : [],
            });

            redirectToExternalUrl(query.redirect || redirectPath);
            return;
          }
        } else {
          setMessage(result.error);
        }
      } catch (err) {
        setMessage(errorMessage(err));
      }

      setAuthenticating(false);
    },
    [redirectPath, accountTypes, query.redirect, getProfile, signInPassword],
  );

  if (!isLocalHostname()) {
    return <NotFoundPage />;
  }

  return (
    <div className="row">
      <div className="container" style={{ marginTop: '200px' }}>
        <form name="sign-in">
          {message && <div className="alert alert-danger">{message}</div>}
          <h3 className="text-center">{localStorage.tenant || 'DEV'}</h3>

          <div>
            <div className="form-group">
              <label key="email-address-label">{translate('Email address')}</label>

              <Input
                id="email"
                key="email-address-input"
                autoComplete="email"
                name="email"
                type="email"
                className="form-control"
                placeholder={translate('Email address')}
                value={email}
                onChange={(evt: any) => {
                  setEmail(evt.target.value);
                  isAutofilled && setIsAutofilled(false);
                }}
                autotrim
              />
            </div>

            <div className="form-group">
              <label key="password-label">{translate('Password')}</label>
              <Input
                id="password"
                autoComplete="password"
                key="password-input"
                name="password"
                type="password"
                className="form-control"
                placeholder={translate('Password')}
                onChange={(evt: any) => {
                  setPassword(evt.target.value);
                  isAutofilled && setIsAutofilled(false);
                }}
                value={password}
                autotrim
              />
            </div>
          </div>

          <div className="form-group">
            <button
              id="sign-in-submit"
              type="submit"
              className="btn btn-block btn-primary"
              disabled={(authenticating || !email || !password) && !isAutofilled}
              onClick={(evt) => authenticate(evt)}
            >
              {authenticating ? translate('PROCESSING...') : translate('Sign in')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default SignInLocal;
